import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { dbService } from "../services/dbService";
import { storageService } from "../services/storageService";
import homeBg from "../assets/images/home.jpeg";

export default function Claim() {
  const { t, i18n } = useTranslation("global");
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [files, setFiles] = useState({
    boardingPass: null,
    reservationProof: null,
    ticketPurchase: null,
    checkout: null,
    identityCard: null,
  });

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const data = await dbService.getUserData(currentUser.uid);
          setUserData(data);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleFileChange = (e) => {
    const { id, files: selectedFiles } = e.target;
    setFiles((prev) => ({ ...prev, [id]: selectedFiles[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userData) return;

    setUploading(true);

    try {
      const basePath = `/Claims/${userData.firstName} ${userData.lastName}_${userData.membership}_${userData.flyxom_membership}/`;
      const promises = [];

      if (files.boardingPass) {
        promises.push(
          storageService.uploadFile(
            basePath + "boardingpass_" + files.boardingPass.name,
            files.boardingPass
          )
        );
      }
      if (files.reservationProof) {
        promises.push(
          storageService.uploadFile(
            basePath + "reservation_" + files.reservationProof.name,
            files.reservationProof
          )
        );
      }
      if (files.ticketPurchase) {
        promises.push(
          storageService.uploadFile(
            basePath + "ticket_" + files.ticketPurchase.name,
            files.ticketPurchase
          )
        );
      }
      if (files.checkout) {
        promises.push(
          storageService.uploadFile(
            basePath + "checkout_" + files.checkout.name,
            files.checkout
          )
        );
      }
      if (files.identityCard) {
        promises.push(
          storageService.uploadFile(
            basePath + "id_" + files.identityCard.name,
            files.identityCard
          )
        );
      }

      await Promise.all(promises);
      alert("Claim submitted successfully!");
      navigate("/profile", { replace: true });
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Error uploading files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-white">
        Loading...
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-center bg-cover bg-no-repeat py-8"
      style={{ backgroundImage: `url(${homeBg})` }}
    >
      <div className="flex items-start justify-center w-full px-4 lg:px-8 pt-20 md:pt-24 pb-12">
        <div className="w-full max-w-4xl bg-gray-800/50 bg-blend-multiply shadow-2xl rounded-lg px-6 md:px-8 lg:px-10 py-8 backdrop-blur-md border border-gray-700/50">
          <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 text-white">
            {t("Claim.text")}
          </h1>

          <div className="flex justify-center mb-6">
            <button
              onClick={() => navigate("/profile", { replace: true })}
              className="flex items-center shadow-xl bg-[#fcab21] hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-lg text-sm md:text-base transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t("Back.text")}
            </button>
          </div>

          <div className="border-t-2 border-gray-600 mb-6"></div>

          <form onSubmit={handleSubmit}>
            <div className="pt-4 mb-4">
              <label
                htmlFor="userName"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("User Name.text")}
              </label>
              <input
                type="text"
                id="userName"
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
                value={
                  userData ? `${userData.firstName} ${userData.lastName}` : ""
                }
                readOnly
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="membership"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("Membership.text")}
              </label>
              <input
                type="text"
                id="membership"
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
                value={userData ? userData.membership : ""}
                readOnly
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="flyxommembership"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("Flyxom_Membership.text")}
              </label>
              <input
                type="text"
                id="flyxommembership"
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
                value={userData ? userData.flyxom_membership : ""}
                readOnly
              />
            </div>

            <div className="mb-4 rounded-lg">
              <label
                htmlFor="identityCard"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("ID.text")}
              </label>
              <input
                id="identityCard"
                accept="image/png,image/jpeg,application/pdf,application/msword,application/msword-document"
                type="file"
                onChange={handleFileChange}
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#fcab21] file:text-white hover:file:bg-yellow-600"
              />
            </div>

            <div className="mb-4 rounded-lg">
              <label
                htmlFor="boardingPass"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("Boarding_Pass.text")}
              </label>
              <input
                id="boardingPass"
                accept="image/png,image/jpeg,application/pdf,application/msword,application/msword-document"
                type="file"
                onChange={handleFileChange}
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#fcab21] file:text-white hover:file:bg-yellow-600"
              />
            </div>

            <div className="mb-4 rounded-lg">
              <label
                htmlFor="ticketPurchase"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("Upload ticket Purchase.text")}
              </label>
              <input
                id="ticketPurchase"
                accept="image/png,image/jpeg,application/pdf,application/msword,application/msword-document"
                type="file"
                onChange={handleFileChange}
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#fcab21] file:text-white hover:file:bg-yellow-600"
              />
            </div>

            <div className="mb-4 rounded-lg">
              <label
                htmlFor="reservationProof"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("Reservation_Proof.text")}
              </label>
              <input
                id="reservationProof"
                accept="image/png,image/jpeg,application/pdf,application/msword,application/msword-document"
                type="file"
                onChange={handleFileChange}
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#fcab21] file:text-white hover:file:bg-yellow-600"
              />
            </div>
            <div className="mb-4 rounded-lg">
              <label
                htmlFor="checkout"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                {t("Upload checkout.text")}
              </label>
              <input
                id="checkout"
                accept="image/png,image/jpeg,application/pdf,application/msword,application/msword-document"
                type="file"
                onChange={handleFileChange}
                className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#fcab21] file:text-white hover:file:bg-yellow-600"
              />
            </div>

            <div className="border-t-2 border-gray-600 pt-6 mt-6"></div>
            <button
              type="submit"
              disabled={uploading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-xl text-sm md:text-base text-white font-semibold bg-[#fcab21] hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#fcab21] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {uploading ? "Uploading..." : t("Send.text")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
